import { Button, ButtonLevel, ButtonSize, ListBox, Skeleton } from '@vizit/atoms';
import { Text, TextColor, TextSize } from '@vizit/atoms';
import { CheckIcon } from '@vizit/icons';
import { Dialog } from '@vizit/molecules';
import { colors } from '@vizit/theme';
import * as React from 'react';
import { ProductCategoryColorSquare, toProductCategoryPath } from '../product-category-picker';
import { describeCategoryPath, findIndistinctCategoryIds, findSharedPathIds } from '../product-category-picker';
import type { PickerProductCategory } from '../product-category-picker';
import { SearchTextField } from '../search-textfield';

/** What adding came to: a category that could not be added stays picked for a retry. */
export interface AddProductCategoriesResult {
  failedCategoryIds: string[];
}

export interface AddProductCategoriesDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Called when the dialog opens or closes
   */
  onOpenChange: (isOpen: boolean) => void;
  /**
   * The line under the title, which says what each added category does to the scores of the images on the page
   */
  subtitle: string;
  /**
   * The categories matching the search: of the organization, or with `isScorableOnly` only the ones the benchmark
   * can score. Not shown before the user types
   */
  categories: PickerProductCategory[];
  /**
   * Whether `categories` holds only the ones the benchmark can score, which the no-match message then says
   *
   * @optional
   * @default false
   */
  isScorableOnly?: boolean;
  /**
   * Categories already added, left out of the list
   *
   * @optional
   */
  excludedCategoryIds?: string[];
  /**
   * How many categories match the search in all, when `categories` is one capped page of them; the list then says it
   * shows only the first matches
   *
   * @optional
   */
  totalCount?: number;
  /**
   * Called with the search text, after a short pause in typing and with `''` when cleared, so the caller can search
   * the API. The dialog lists nothing until there is a search, and filters the rows it has meanwhile
   *
   * @optional
   */
  onSearchChange?: (search: string) => void;
  /**
   * Whether the categories are still loading
   *
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the API is still answering the current search while `categories` are the previous search's. The rows
   * stay up meanwhile, and "nothing matches" waits for the answer
   *
   * @optional
   * @default false
   */
  isSearching?: boolean;
  /**
   * Whether the categories failed to load; the list then says so and offers "Try again" when `onRetry` is set, and
   * adding waits, keeping the pick, until the list is back
   *
   * @optional
   * @default false
   */
  isLoadError?: boolean;
  /**
   * Called from the load error's "Try again"
   *
   * @optional
   */
  onRetry?: () => void;
  /**
   * Error from the last add attempt
   *
   * @optional
   */
  errorMessage?: string | null;
  /**
   * Adds the picked category, as a one-item list; the dialog shows a loading state until it settles. It closes once
   * the category is added and otherwise keeps it picked for a retry, as a rejection does.
   */
  onAdd: (categoryIds: string[]) => Promise<AddProductCategoriesResult | undefined>;
}

/** Why a search lists nothing: every match is already added, or nothing matches. */
function emptyListMessage(term: string, matchCount: number, isScorableOnly: boolean) {
  if (matchCount > 0) {
    return `Every product category matching "${term}" is already added.`;
  }
  return isScorableOnly ? `No scorable product categories match "${term}".` : `No product categories match "${term}".`;
}

/**
 * Picks one product category to score a benchmark or a collection against, so adding one is a deliberate act.
 * Search comes first: nothing is listed until the user types, then a single-choice list of the matching categories
 * minus the ones already added. Each row shows the category's name and, under it, the categories above it (and its
 * retailer, only when that alone tells it apart), wrapping onto more lines rather than scrolling sideways. The list
 * keeps one fixed height in every state, so the dialog never resizes. Shared by the benchmark page and the collection
 * page.
 */
export function AddProductCategoriesDialog(props: AddProductCategoriesDialogProps) {
  const {
    isOpen,
    onOpenChange,
    subtitle,
    categories,
    isScorableOnly = false,
    excludedCategoryIds = [],
    totalCount,
    onSearchChange,
    isLoading = false,
    isSearching = false,
    isLoadError = false,
    onRetry,
    errorMessage,
    onAdd,
  } = props;

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [isAdding, setIsAdding] = React.useState(false);
  // Set only while rows are listed, so ArrowDown from the search has somewhere to go.
  const listRef = React.useRef<HTMLDivElement>(null);

  // Kept in a ref so closing, not a new callback, is what resets the caller's search.
  const onSearchChangeRef = React.useRef(onSearchChange);
  React.useLayoutEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setSearch('');
      onSearchChangeRef.current?.('');
    }
  }, [isOpen]);

  const handleSearch = (value: string) => {
    // The field stays mounted while the dialog animates out, so a search typed just before closing can arrive
    // after the reset above; the next opening starts from an empty search instead.
    if (!isOpen) {
      return;
    }
    setSearch(value);
    onSearchChange?.(value);
  };

  // Each category's path as searched, worked out once per list rather than per row and keystroke.
  const rows = React.useMemo(
    () => categories.map((category) => ({ category, searchPath: toProductCategoryPath(category).toLowerCase() })),
    [categories],
  );
  const term = search.trim();
  const needle = term.toLowerCase();
  // As the API's search: the name or the path contains it, ignoring case. Nothing is listed before a search.
  const matching = needle ? rows.filter((row) => row.searchPath.includes(needle)) : [];
  const excluded = new Set(excludedCategoryIds);
  // The line under each name and its accessible name, as the picker's rows have them: the retailer only where it
  // alone tells two matches apart. One already added counts, as the picker lists it beside this one once it is added.
  const sharedPathIds = findSharedPathIds(matching.map(({ category }) => category));
  const listed = matching.filter((row) => !excluded.has(row.category.id)).map(({ category }) => category);
  const visible = listed.map((category) => ({
    category,
    ...describeCategoryPath(category, { withRetailer: sharedPathIds.has(category.id) }),
  }));
  const indistinctIds = findIndistinctCategoryIds(visible);
  // Only a pick in view can be added, so the primary never adds a row the search has hidden; it comes back with the
  // row.
  const pickedId = visible.some((row) => row.category.id === selectedId) ? selectedId : null;
  // A broad search can match more than one page; the rest are only reached by typing more. While a new search loads,
  // the count is the previous one's. It says no number: the rows shown are the page minus the ones already added.
  const isCapped = totalCount !== undefined && totalCount > categories.length && !isSearching;

  const handleAdd = async () => {
    if (!pickedId) {
      return;
    }
    setIsAdding(true);
    try {
      const result = await onAdd([pickedId]);
      if ((result?.failedCategoryIds ?? []).length === 0) {
        onOpenChange(false);
      }
    } catch {
      // The caller reports the failure through `errorMessage`; the dialog stays open to retry.
    } finally {
      setIsAdding(false);
    }
  };

  const renderList = () => {
    if (isLoadError) {
      return (
        <div role="alert" className="flex flex-col items-start gap-4">
          <Text size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
            Product categories couldn't load.
          </Text>
          {onRetry && (
            <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={() => onRetry()}>
              Try again
            </Button>
          )}
        </div>
      );
    }
    if (!term) {
      return (
        <Text size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
          Type to find a product category.
        </Text>
      );
    }
    if (isLoading || (isSearching && visible.length === 0)) {
      return (
        <output className="flex flex-col gap-4" aria-label="Loading product categories">
          <Skeleton variant="square" className="h-10 w-2/3" />
          <Skeleton variant="square" className="h-10 w-1/2" />
          <Skeleton variant="square" className="h-10 w-3/5" />
        </output>
      );
    }
    if (visible.length === 0) {
      return (
        <Text size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
          {emptyListMessage(term, matching.length, isScorableOnly)}
        </Text>
      );
    }
    return (
      <>
        {isCapped && (
          <Text size={TextSize.Xs} color={TextColor.Tertiary} showEllipsis={false}>
            More categories match than are listed. Keep typing to narrow them.
          </Text>
        )}
        <ListBox
          ref={listRef}
          aria-label="Product categories"
          selectionMode="single"
          // Arrow keys move the pick along, and it cannot be cleared by pressing it again: one choice, as radios work.
          selectionBehavior="replace"
          disallowEmptySelection
          selectedKeys={pickedId ? [pickedId] : []}
          onSelectionChange={(keys) => {
            if (keys !== 'all') {
              setSelectedId((keys.values().next().value as string | undefined) ?? null);
            }
          }}
          // The rows hold still while the pick is being added.
          disabledKeys={isAdding ? visible.map((row) => row.category.id) : undefined}
          className="-mx-7 border-none py-0 shadow-none"
        >
          {visible.map(({ category, path, label }) => {
            const idNote = indistinctIds.has(category.id) ? `ID ${category.id}` : '';
            const details = [path, idNote].filter(Boolean).join(' · ');
            return (
              <ListBox.Item
                key={category.id}
                id={category.id}
                textValue={category.name}
                aria-label={[label, idNote].filter(Boolean).join(', ')}
                className="h-fit pr-16"
              >
                {({ isSelected }) => (
                  // Shrinks to the row, so a long name or path wraps onto more lines instead of scrolling sideways.
                  <span className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 shrink-0 items-center">
                      <ProductCategoryColorSquare color={category.color} />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <Text size={TextSize.Sm} showEllipsis={false} className="[overflow-wrap:anywhere]">
                        {category.name}
                      </Text>
                      {details && (
                        <Text
                          size={TextSize.Xs}
                          color={TextColor.Tertiary}
                          showEllipsis={false}
                          className="[overflow-wrap:anywhere]"
                        >
                          {details}
                        </Text>
                      )}
                    </span>
                    <span className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center">
                      {isSelected && <CheckIcon size={16} color={colors.brand['600']} />}
                    </span>
                  </span>
                )}
              </ListBox.Item>
            );
          })}
        </ListBox>
      </>
    );
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Overlay isDismissable={!isAdding}>
        <Dialog.Content closeButtonIsDisabled={isAdding} dialogClassName="gap-8">
          <Dialog.Header>
            <Dialog.Title>Add a product category</Dialog.Title>
            <Dialog.Subtitle>{subtitle}</Dialog.Subtitle>
          </Dialog.Header>
          <div className="px-12">
            <SearchTextField
              // Search comes first, so typing goes straight into it.
              autoFocus
              variant="filled"
              placeholder="Search product categories"
              debounceDelay={300}
              onSearch={handleSearch}
              onKeyDown={(event) => {
                // Into the list, onto the pick or else the first row. Not while adding, when every row is disabled,
                // nor while an input method is composing, which may use ArrowDown for its own candidates.
                if (event.key === 'ArrowDown' && listRef.current && !isAdding && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  // The list remembers the row it last focused, which a search can leave on a row other than the pick.
                  const picked = listRef.current.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
                  (picked ?? listRef.current).focus();
                  return;
                }
                // react-aria stops every key at a handler like this one; let the rest through, so Escape still closes.
                event.continuePropagation();
              }}
            />
          </div>
          {/* One fixed height for the list in every state, so results never resize the dialog. It still shrinks on a
              short viewport, where the list scrolls. */}
          <div className="flex h-[360px] min-h-0 flex-col gap-6">
            <Dialog.Body className="flex flex-1 flex-col gap-6 px-12">{renderList()}</Dialog.Body>
            {/* Outside the scrolling list, so it shows wherever the pick was; the list gives up the room it takes. */}
            {errorMessage && (
              <Text role="alert" size={TextSize.Sm} className="shrink-0 px-12 text-error-primary" showEllipsis={false}>
                {errorMessage}
              </Text>
            )}
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={() => onOpenChange(false)}
              isDisabled={isAdding}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Primary}
              onPress={handleAdd}
              // Adding waits for the list: a load error can mean the benchmark is gone, which adding would only repeat.
              isDisabled={!pickedId || isLoadError}
              isLoading={isAdding}
            >
              Add category
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}
